import { DAYS_IN_WEEK, MINUTES_IN_DAY, MINUTES_IN_HOUR } from 'utils/constants'
import { fromUTCMidnightSeconds } from 'utils/time'
import moment from 'moment'
import { TIME } from './constants'

export const formatTimeOfDay = timeStr => {
  if (!timeStr || typeof timeStr !== 'string') return null
  const trimmed = timeStr.trim()
  if (!trimmed) return null
  const m = moment(trimmed, ['HH:mm', 'H:mm', 'HH:mm:ss'], true)
  if (!m.isValid()) return trimmed
  const h = m.hour()
  const min = m.minute()
  if (min === 0) return h === 12 ? '12pm' : h === 0 ? '12am' : h < 12 ? `${h}am` : `${h - 12}pm`
  return h === 12 ? `12:${min.toString().padStart(2, '0')}pm` : h === 0 ? `12:${min.toString().padStart(2, '0')}am` : h < 12 ? `${h}:${min.toString().padStart(2, '0')}am` : `${h - 12}:${min.toString().padStart(2, '0')}pm`
}

const parseClockTime = timeStr => {
  if (!timeStr || typeof timeStr !== 'string') return null
  const parsed = moment(timeStr.trim(), ['HH:mm', 'H:mm', 'HH:mm:ss'], true)
  return parsed.isValid() ? parsed : null
}

const minutesUntilEndOfDay = timeOfDay => {
  const start = parseClockTime(timeOfDay)
  if (!start) return MINUTES_IN_DAY - 1
  const end = start.clone().hour(23).minute(59)
  return Math.max(1, end.diff(start, 'minutes'))
}

const intervalToMinutes = (interval, intervalType, { subtractStartDay = false, timeOfDay } = {}) => {
  const value = Number(interval)
  if (!Number.isFinite(value) || value < 0) return null
  const type = String(intervalType || 'days').toLowerCase()
  if (type.startsWith('minute')) return value
  if (type.startsWith('hour')) return value * MINUTES_IN_HOUR
  if (type.startsWith('week')) return value * DAYS_IN_WEEK * MINUTES_IN_DAY
  const days = subtractStartDay ? Math.max(0, value - 1) : value
  if (days === 0) return minutesUntilEndOfDay(timeOfDay)
  return days * MINUTES_IN_DAY
}

const resolveExpireStartMoment = (schedule, instrument, options = {}) => {
  const deploy = schedule?.deploy || {}
  if (deploy.absolute != null) {
    try {
      const start = fromUTCMidnightSeconds(deploy.absolute)
      if (start?.isValid()) return start
    } catch (_) {
      /* fall through */
    }
  }
  if (instrument?.deployed) {
    const deployed = moment.utc(instrument.deployed)
    if (deployed.isValid()) return deployed
  }
  if (options.useAbsoluteStartDate && options.absoluteStartDate) {
    const startDate = moment.utc(options.absoluteStartDate)
    if (startDate.isValid()) return startDate
  }
  return null
}

const calendarDaysBetween = (startMoment, endMoment) => {
  if (!startMoment?.isValid() || !endMoment?.isValid()) return null
  return endMoment.clone().startOf('day').diff(startMoment.clone().startOf('day'), 'days')
}

export const formatRelativeExpirySuffix = totalMinutes => {
  if (totalMinutes == null || !Number.isFinite(totalMinutes)) {
    return `(+${TIME.NEVER_EXPIRE})`
  }
  const minutes = Math.max(0, totalMinutes)
  if (minutes < MINUTES_IN_HOUR) {
    return `(+${Math.max(1, Math.round(minutes))}m)`
  }
  if (minutes < MINUTES_IN_DAY) {
    return `(+${Math.max(1, Math.round(minutes / MINUTES_IN_HOUR))}h)`
  }
  const days = Math.round(minutes / MINUTES_IN_DAY)
  if (days < DAYS_IN_WEEK) {
    return `(+${days}d)`
  }
  const weeks = Math.floor(days / DAYS_IN_WEEK)
  const leftoverDays = days % DAYS_IN_WEEK
  return leftoverDays === 0 ? `(+${weeks}w)` : `(+${weeks}w ${leftoverDays}d)`
}

export const getExpiryDurationMinutes = (schedule, instrument = null, options = {}) => {
  const expire = schedule?.expire || {}
  const deploy = schedule?.deploy || {}

  if (expire.relative_to_send_ideal) {
    return intervalToMinutes(expire.relative_to_send_ideal.interval, expire.relative_to_send_ideal.interval_type, {
      subtractStartDay: true,
      timeOfDay: deploy.time_of_day,
    })
  }
  if (expire.first_login && deploy.first_login) {
    return intervalToMinutes(
      Number(expire.first_login.interval) - Number(deploy.first_login.interval),
      expire.first_login.interval_type || deploy.first_login.interval_type,
      { timeOfDay: deploy.time_of_day },
    )
  }
  if (expire.chain_expire_offset != null && deploy.chain_deploy_offset != null) {
    return intervalToMinutes(Number(expire.chain_expire_offset) - Number(deploy.chain_deploy_offset), 'days', {
      timeOfDay: deploy.time_of_day,
    })
  }
  if (expire.absolute != null) {
    try {
      const endMoment = fromUTCMidnightSeconds(expire.absolute)
      const startMoment = resolveExpireStartMoment(schedule, instrument, options)
      const days = calendarDaysBetween(startMoment, endMoment)
      if (days != null) {
        if (days <= 0) return minutesUntilEndOfDay(deploy.time_of_day)
        return days * MINUTES_IN_DAY
      }
    } catch (_) {
      /* fall through */
    }
  }
  if (expire.expire_type && expire.days_offset != null) {
    return intervalToMinutes(expire.days_offset, 'days', { timeOfDay: deploy.time_of_day })
  }

  return null
}

export const getInstrumentTimeWindowString = (schedule, instrument = null, options = {}) => {
  const startFormatted = formatTimeOfDay(schedule?.deploy?.time_of_day)
  if (!startFormatted) return null
  return `${startFormatted} ${formatRelativeExpirySuffix(getExpiryDurationMinutes(schedule, instrument, options))}`
}
